import { Typography, TypographyProps } from '@carto/meridian-ds/components'
import { ICON_SIZE_SMALL } from '@carto/meridian-ds/theme'
import { styled, Theme } from '@mui/material'
import { ReactNode } from 'react'

export type TagColor = 'primary' | 'secondary' | 'error' | 'success' | 'warning' | 'default'

export type TagVariant = 'filled' | 'outlined'

function colorProps(color: TagColor, variant: TagVariant, theme: Theme) {
  switch (color) {
    case 'default':
      if (variant === 'outlined') {
        return {
          color: theme.palette.text.secondary,
          backgroundColor: theme.palette.black['4'],
          borderColor: theme.palette.divider
        }
      }

      return {
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.default.main
      }
    case 'primary':
      if (variant === 'outlined') {
        return {
          color: theme.palette.primary.main,
          backgroundColor: theme.palette.primary.relatedLight,
          borderColor: theme.palette.primary.main
        }
      }

      return {
        color: theme.palette.primary.contrastText,
        backgroundColor: theme.palette.primary.main
      }
    case 'secondary':
      if (variant === 'outlined') {
        return {
          color: theme.palette.secondary.main,
          backgroundColor: theme.palette.secondary.relatedLight,
          borderColor: theme.palette.secondary.main
        }
      }

      return {
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.secondary.main
      }
    case 'success':
      if (variant === 'outlined') {
        return {
          color: theme.palette.success.main,
          backgroundColor: theme.palette.success.relatedLight,
          borderColor: theme.palette.success.main
        }
      }

      return {
        color: theme.palette.success.main,
        backgroundColor: theme.palette.success.relatedLight
      }

    case 'warning':
      if (variant === 'outlined') {
        return {
          color: theme.palette.warning.main,
          backgroundColor: theme.palette.warning.relatedLight,
          borderColor: theme.palette.warning.main
        }
      }

      return {
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.warning.main
      }
    case 'error':
      if (variant === 'outlined') {
        return {
          color: theme.palette.error.main,
          backgroundColor: theme.palette.error.relatedLight,
          borderColor: theme.palette.error.main
        }
      }

      return {
        color: theme.palette.primary.contrastText,
        backgroundColor: theme.palette.error.main
      }
  }
}

const TagRoot = styled('div', {
  shouldForwardProp: (prop) => !['color', 'variant'].includes(prop as string)
})<{ color?: TagColor; variant?: TagVariant }>(({ color, variant, theme }) => {
  return {
    display: 'inline-flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    padding: theme.spacing(0, 0.5),
    borderRadius: theme.spacing(0.25),
    gap: theme.spacing(0.5),
    minHeight: theme.spacing(2),
    borderWidth: variant === 'outlined' ? '1px' : 0,
    borderStyle: 'solid',
    ...colorProps(color ?? 'primary', variant ?? 'filled', theme)
  }
})

const TagIcon = styled('div')(({ theme }) => ({
  alignSelf: 'center',
  '& svg': {
    width: theme.spacing(ICON_SIZE_SMALL),
    height: theme.spacing(ICON_SIZE_SMALL),
    '& g': {
      fill: 'currentColor'
    }
  }
}))

const TagLabel = styled('div', { shouldForwardProp: (prop) => prop !== 'noWrap' })<{ noWrap?: boolean }>(
  ({ noWrap, theme }) => ({
    whiteSpace: noWrap ? 'nowrap' : undefined,
    paddingTop: theme.spacing(0.25),
    alignSelf: 'center',
    lineHeight: theme.spacing(2),
    ...theme.typography.code3,
    fontWeight: theme.typography.fontWeightBold
  })
)

// on omitting as: typings for styled(span) doesn't accept 'as', work-it-around
export type TagProps = Omit<React.HTMLProps<HTMLDivElement>, 'as' | 'size' | 'label'> & {
  /** Label, main content of tag, by default displayed as `caption` type typography.  */
  label: ReactNode
  color?: TagColor
  icon?: ReactNode
  variant?: TagVariant
  /** Options passed for typograpy, use to override default `caption` style */
  typographyProps?: TypographyProps
  noWrap?: boolean
}

function Tag({ label, color, variant, icon, typographyProps, noWrap = true, ...otherProps }: TagProps) {
  return (
    // Don't remove className, it's used for styling
    <TagRoot className="CartoTag-root" color={color} variant={variant} {...otherProps}>
      {icon && <TagIcon>{icon}</TagIcon>}
      <TagLabel noWrap={noWrap}>
        <Typography color="inherit" variant="caption" {...typographyProps}>
          {label}
        </Typography>
      </TagLabel>
    </TagRoot>
  )
}

export default Tag
